import { Pagination } from '@/dsm-components/pagination';
import { useGetPastDueAccountsQuery } from '@/services/pastDueAccounts';
import type { DaysPastDueBucket, PastDueAccount, PastDueCategory } from '@/services/pastDueAccounts';
import { PalmettoTag, PalmettoTextInput } from '@palmetto/dsm-components';
import { DateTime } from 'luxon';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Link } from 'react-router-dom';

import { DsmPageContainer } from '../../dsm-components/layout/DsmPageContainer';
import PageHeader from '../PageHeader';

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 50;

type TileKey = 'all' | PastDueCategory;
type TagColor = 'warning' | 'error' | 'palmetto' | 'grey';
type TagEmphasis = 'regular' | 'bold';

const CATEGORY_LABEL: Record<PastDueCategory, string> = {
  earlyStage: 'Early stage',
  lateStage: 'Late stage collections',
  paymentPlan: 'Payment plan',
  bankruptcy: 'Bankruptcy',
};

const CATEGORY_TAG_COLOR: Record<PastDueCategory, TagColor> = {
  earlyStage: 'warning',
  lateStage: 'error',
  paymentPlan: 'palmetto',
  bankruptcy: 'grey',
};

const CATEGORY_TAG_EMPHASIS: Record<PastDueCategory, TagEmphasis> = {
  earlyStage: 'regular',
  lateStage: 'regular',
  paymentPlan: 'regular',
  bankruptcy: 'bold',
};

const TILES: { key: TileKey; label: string }[] = [
  { key: 'all', label: 'All accounts' },
  { key: 'earlyStage', label: CATEGORY_LABEL.earlyStage },
  { key: 'lateStage', label: CATEGORY_LABEL.lateStage },
  { key: 'paymentPlan', label: CATEGORY_LABEL.paymentPlan },
  { key: 'bankruptcy', label: CATEGORY_LABEL.bankruptcy },
];

type BucketKey = 'all' | DaysPastDueBucket;

const BUCKET_LABEL: Record<DaysPastDueBucket, string> = {
  days6To30: '6–30 days',
  days31To60: '31–60 days',
  days61To90: '61–90 days',
  days91To120: '91–120 days',
};

const BUCKET_CHIPS: { key: BucketKey; label: string }[] = [
  { key: 'all', label: 'All early stage' },
  { key: 'days6To30', label: BUCKET_LABEL.days6To30 },
  { key: 'days31To60', label: BUCKET_LABEL.days31To60 },
  { key: 'days61To90', label: BUCKET_LABEL.days61To90 },
  { key: 'days91To120', label: BUCKET_LABEL.days91To120 },
];

const TILE_BASE_CLASS =
  'flex cursor-pointer flex-col items-start gap-4 rounded-[8px] border px-[16px] py-12 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-text-info';
const TILE_SELECTED_CLASS = 'border-text-info bg-canvas-highlight-default ring-1 ring-text-info';
const TILE_IDLE_CLASS = 'border-border-default bg-canvas-subtle hover:bg-canvas-highlight-default';

const CHIP_BASE_CLASS =
  'flex cursor-pointer items-center gap-8 rounded-[8px] border px-[12px] py-4 text-left text-[13px] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-text-info';

const HEAD_CELL_CLASS =
  'whitespace-nowrap border-b border-border-default bg-canvas-default px-[16px] py-10 text-left text-[12px] font-semibold uppercase tracking-wide text-text-secondary';
const ACCOUNT_LINK_CLASS =
  'rounded-[2px] font-medium text-text-info! underline decoration-text-info! decoration-1 underline-offset-2 hover:decoration-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-text-info';
const CELL_CLASS = 'whitespace-nowrap border-b border-border-default px-[16px] py-10 text-text-primary';
const MONO_CELL_CLASS = `${CELL_CLASS} font-mono text-[13px]`;

const PAGE_SIZE_SELECT_CLASS =
  'cursor-pointer rounded-[6px] border border-border-default bg-canvas-default px-[8px] py-4 text-[13px] text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-text-info';

const formatSnapshot = (iso: string) =>
  `${DateTime.fromISO(iso, { zone: 'utc' }).toFormat('LLL d, yyyy HH:mm')} UTC`;

interface TileButtonProps {
  tileKey: TileKey;
  label: string;
  count: number;
  selected: boolean;
  onSelect: (key: TileKey) => void;
}

const TileButton = memo(function TileButton({ tileKey, label, count, selected, onSelect }: TileButtonProps) {
  const handleClick = useCallback(() => onSelect(tileKey), [onSelect, tileKey]);

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={handleClick}
      className={`${TILE_BASE_CLASS} ${selected ? TILE_SELECTED_CLASS : TILE_IDLE_CLASS}`}
    >
      <span className="body-secondary text-[13px] text-text-secondary">{label}</span>
      <span className="text-[28px] leading-none font-semibold text-text-primary tabular-nums">{count}</span>
    </button>
  );
});

interface BucketChipProps {
  bucketKey: BucketKey;
  label: string;
  count: number;
  selected: boolean;
  onSelect: (key: BucketKey) => void;
}

const BucketChip = memo(function BucketChip({ bucketKey, label, count, selected, onSelect }: BucketChipProps) {
  const handleClick = useCallback(() => onSelect(bucketKey), [onSelect, bucketKey]);

  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={handleClick}
      className={`${CHIP_BASE_CLASS} ${selected ? TILE_SELECTED_CLASS : TILE_IDLE_CLASS}`}
    >
      <span className="text-text-secondary">{label}</span>
      <span className="font-semibold text-text-primary tabular-nums">{count}</span>
    </button>
  );
});

interface AccountRowProps {
  account: PastDueAccount;
  showDaysPastDue: boolean;
}

const AccountRow = memo(function AccountRow({ account, showDaysPastDue }: AccountRowProps) {
  return (
    <tr className="hover:bg-canvas-default">
      <td className={CELL_CLASS}>
        <PalmettoTag
          label={CATEGORY_LABEL[account.category]}
          color={CATEGORY_TAG_COLOR[account.category]}
          emphasis={CATEGORY_TAG_EMPHASIS[account.category]}
          size="sm"
        />
      </td>
      {showDaysPastDue && (
        <td className={CELL_CLASS}>{account.daysPastDueBucket && BUCKET_LABEL[account.daysPastDueBucket]}</td>
      )}
      <td className={MONO_CELL_CLASS}>{account.concordAccountId}</td>
      <td className={MONO_CELL_CLASS}>
        <Link className={ACCOUNT_LINK_CLASS} to={`/accounts/${account.finCoAccountId}/customer-payments`}>
          {account.finCoAccountId}
        </Link>
      </td>
    </tr>
  );
});

const Message = ({ children }: { children: string }) => (
  <div className="body-secondary p-[24px] text-text-secondary">{children}</div>
);

export function PastDueAccounts() {
  const { data, isLoading, isError } = useGetPastDueAccountsQuery();
  const [selectedTile, setSelectedTile] = useState<TileKey>('all');
  const [selectedBucket, setSelectedBucket] = useState<BucketKey>('all');
  const [search, setSearch] = useState('');
  const [requestedPage, setRequestedPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const cardRef = useRef<HTMLDivElement>(null);

  const accounts = useMemo<PastDueAccount[]>(() => data?.accounts ?? [], [data]);

  const counts = useMemo(() => {
    const result: Record<TileKey, number> = {
      all: accounts.length,
      earlyStage: 0,
      lateStage: 0,
      paymentPlan: 0,
      bankruptcy: 0,
    };
    accounts.forEach((account) => {
      result[account.category] += 1;
    });
    return result;
  }, [accounts]);

  const bucketCounts = useMemo(() => {
    const result: Record<BucketKey, number> = {
      all: 0,
      days6To30: 0,
      days31To60: 0,
      days61To90: 0,
      days91To120: 0,
    };
    accounts.forEach((account) => {
      if (account.category === 'earlyStage') {
        result.all += 1;
        if (account.daysPastDueBucket) {
          result[account.daysPastDueBucket] += 1;
        }
      }
    });
    return result;
  }, [accounts]);

  const visibleAccounts = useMemo(() => {
    const query = search.trim().toLowerCase();
    return accounts.filter((account) => {
      if (selectedTile !== 'all' && account.category !== selectedTile) {
        return false;
      }
      if (
        selectedTile === 'earlyStage' &&
        selectedBucket !== 'all' &&
        account.daysPastDueBucket !== selectedBucket
      ) {
        return false;
      }
      if (!query) {
        return true;
      }
      return [account.finCoAccountId, account.concordAccountId, CATEGORY_LABEL[account.category]].some((value) =>
        value.toLowerCase().includes(query),
      );
    });
  }, [accounts, selectedTile, selectedBucket, search]);

  const totalPages = Math.max(1, Math.ceil(visibleAccounts.length / pageSize));
  const page = Math.min(requestedPage, totalPages - 1);

  const pageAccounts = useMemo(
    () => visibleAccounts.slice(page * pageSize, (page + 1) * pageSize),
    [visibleAccounts, page, pageSize],
  );

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setRequestedPage(0);
  }, []);

  const handleTileSelect = useCallback((key: TileKey) => {
    setSelectedTile(key);
    setSelectedBucket('all');
    setRequestedPage(0);
  }, []);

  const handleBucketSelect = useCallback((key: BucketKey) => {
    setSelectedBucket(key);
    setRequestedPage(0);
  }, []);

  const handlePageSizeChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const nextSize = Number(event.target.value);
    if (!PAGE_SIZE_OPTIONS.includes(nextSize)) {
      return;
    }
    setPageSize(nextSize);
    setRequestedPage(0);
  }, []);

  const handlePageChange = useCallback((nextPage: number) => {
    setRequestedPage(nextPage);
    cardRef.current?.scrollIntoView?.({ block: 'start' });
  }, []);

  const showDaysPastDue = selectedTile === 'earlyStage';

  const rangeStart = visibleAccounts.length === 0 ? 0 : page * pageSize + 1;
  const rangeEnd = Math.min((page + 1) * pageSize, visibleAccounts.length);

  const renderBody = () => {
    if (isLoading) {
      return <Message>Loading past due accounts...</Message>;
    }
    if (isError) {
      return <Message>Unable to load past due accounts. Please try again.</Message>;
    }
    if (!data?.snapshotTimestamp) {
      return <Message>No snapshot is available yet.</Message>;
    }

    return (
      <>
        <div className="grid grid-cols-2 gap-12 md:grid-cols-3 lg:grid-cols-5">
          {TILES.map((tile) => (
            <TileButton
              key={tile.key}
              tileKey={tile.key}
              label={tile.label}
              count={counts[tile.key]}
              selected={selectedTile === tile.key}
              onSelect={handleTileSelect}
            />
          ))}
        </div>

        {selectedTile === 'earlyStage' && (
          <div role="group" aria-label="Days past due" className="flex flex-wrap items-center gap-8">
            {BUCKET_CHIPS.map((chip) => (
              <BucketChip
                key={chip.key}
                bucketKey={chip.key}
                label={chip.label}
                count={bucketCounts[chip.key]}
                selected={selectedBucket === chip.key}
                onSelect={handleBucketSelect}
              />
            ))}
          </div>
        )}

        <div
          ref={cardRef}
          className="overflow-hidden rounded-16 border border-border-default bg-canvas-subtle shadow-[0px_2px_0px_0px_var(--canvas-highlight-bold)]"
        >
          <div className="border-b border-border-default p-[16px]">
            <PalmettoTextInput
              label="Search accounts"
              aria-label="Search accounts"
              placeholder="Search by Sabal or Concord account ID"
              value={search}
              onChange={handleSearchChange}
            />
          </div>
          <div className="overflow-x-auto">
            <table aria-label="Past due accounts" className="body-primary w-full border-collapse text-[14px]">
              <thead>
                <tr>
                  <th className={HEAD_CELL_CLASS}>Category</th>
                  {showDaysPastDue && <th className={HEAD_CELL_CLASS}>Days past due</th>}
                  <th className={HEAD_CELL_CLASS}>Concord account</th>
                  <th className={HEAD_CELL_CLASS}>Sabal account</th>
                </tr>
              </thead>
              <tbody>
                {pageAccounts.map((account) => (
                  <AccountRow
                    key={`${account.category}-${account.finCoAccountId}`}
                    account={account}
                    showDaysPastDue={showDaysPastDue}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {visibleAccounts.length === 0 && <Message>No accounts match these filters.</Message>}
          {visibleAccounts.length > 0 && (
            <div className="body-secondary flex flex-wrap items-center justify-between gap-12 border-t border-border-default px-[16px] py-12 text-text-secondary">
              <span className="tabular-nums">{`Showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${visibleAccounts.length.toLocaleString()}`}</span>
              <div className="flex flex-wrap items-center gap-16">
                <label className="flex items-center gap-8" htmlFor="past-due-page-size">
                  Rows per page
                  <select
                    id="past-due-page-size"
                    className={PAGE_SIZE_SELECT_CLASS}
                    value={pageSize}
                    onChange={handlePageSizeChange}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </label>
                <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <DsmPageContainer title="Past Due Accounts">
      <div className="flex flex-wrap items-end justify-between gap-16">
        <PageHeader
          title="Past Due Accounts"
          description="Accounts Concord currently reports in irregular standing, from the most recent snapshot."
        />
        {data?.snapshotTimestamp && (
          <div className="body-secondary text-[13px] text-text-secondary lg:text-right">
            <div>
              Snapshot taken <b className="text-text-primary">{formatSnapshot(data.snapshotTimestamp)}</b>
            </div>
            <div>Refreshed every 6 hours from Concord</div>
          </div>
        )}
      </div>
      {renderBody()}
    </DsmPageContainer>
  );
}
